import type { TourControlDescription, TourControlInput } from "../services/ApiService";
import { getAppVersion } from "./appVersion";
import { ScannedControl } from "./controlScanner";
import {
  SCREEN_KEYS,
  Walkthrough,
  WalkthroughContext,
  WalkthroughStep,
  walkthroughFor,
} from "./walkthroughs";

/**
 * WHERE EACH STEP OF A SCANNED TOUR GETS ITS WORDS.
 *
 * Three sources, best first, and the tour never waits for any of them:
 *
 * 1. **Curated** — the control carries a `data-csm-step` anchor that a hand-written walkthrough
 *    describes. That text was read off the component (`walkthroughs.ts`), so it wins outright
 *    and is never sent to the model.
 * 2. **Remembered** — CoSMo described this control before, on this app version. Kept in
 *    `localStorage` so the second tour of a screen is instant and costs nothing.
 * 3. **CoSMo** — everything else is sent in one batch (`describeTourControls`). Until it
 *    answers, the step shows a sentence built from the control's own label and kind, with a
 *    "CoSMo is describing this…" line under it; if it never answers, that sentence stays.
 *
 * The chat panel is never involved: the words land in the spotlight card.
 *
 * ⚠️ ONLY SOURCE 3 LEAVES THE BROWSER, AND EVERYTHING THAT DOES IS A GAP. A control reaches the
 * Function App only because no curated step describes it, so the server records each one under
 * `tour-gaps/{screen}/` in the support blob container (`Support/TourGapStore.cs`), with CoSMo's
 * wording as a draft — that folder is the to-do list for `walkthroughs.ts`. Add the step there
 * (and the `data-csm-step` anchor, if the record has none) and the control stops being sent.
 */

export type StepSource = "curated" | "remembered" | "pending" | "label";

export interface TourStep extends WalkthroughStep {
  /** The scanner's id; the overlay finds the element by `TOUR_ATTRIBUTE`. */
  readonly scanId: string;
  readonly source: StepSource;
}

export interface TourPlan {
  readonly walkthrough: Walkthrough;
  /** What still needs CoSMo, in step order. Empty when every step is curated or remembered. */
  readonly toDescribe: TourControlInput[];
}

const CACHE_KEY = "csm.tour.descriptions";
const CACHE_LIMIT = 600;

type CacheShape = { version: string; entries: Record<string, { title?: string; body: string }> };

const cacheSignature = (screenKey: string | undefined, control: ScannedControl): string =>
  [screenKey ?? "", control.kind, control.label.toLowerCase().replace(/\d+/g, "#"), control.context ?? "", control.state ?? ""].join("|");

const readCache = (): CacheShape => {
  const version = getAppVersion();
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    const parsed = raw ? (JSON.parse(raw) as CacheShape) : null;
    // A new release may have changed what a control does; forget everything it wrote.
    if (parsed && parsed.version === version && parsed.entries && typeof parsed.entries === "object") {
      return parsed;
    }
  } catch {
    // Storage unavailable or corrupt: behave as an empty cache.
  }
  return { version, entries: {} };
};

const writeCache = (cache: CacheShape): void => {
  try {
    const keys = Object.keys(cache.entries);
    if (keys.length > CACHE_LIMIT) {
      // Insertion order is age order; drop the oldest.
      for (const key of keys.slice(0, keys.length - CACHE_LIMIT)) delete cache.entries[key];
    }
    window.localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Quota or privacy mode. The tour works exactly the same, it just asks again next time.
  }
};

/** Test seam. */
export const __clearTourCache = (): void => {
  try {
    window.localStorage.removeItem(CACHE_KEY);
  } catch {
    /* ignore */
  }
};

/**
 * Every curated step text, by anchor: the current screen's own tour first (its wording is about
 * THIS screen), then every other tour, so a header or ribbon control is described the same way
 * wherever it is found.
 */
const curatedIndex = (screenKey: string | undefined, context: WalkthroughContext): Map<string, WalkthroughStep> => {
  const index = new Map<string, WalkthroughStep>();
  const add = (walkthrough: Walkthrough | null) => {
    walkthrough?.steps.forEach((step) => {
      if (!index.has(step.anchor)) index.set(step.anchor, step);
    });
  };

  add(walkthroughFor(screenKey, context));
  for (const key of SCREEN_KEYS) {
    add(walkthroughFor(key));
    if (key === "home") {
      add(walkthroughFor(key, { isManagerMode: true, isActiveJobMode: true }));
      add(walkthroughFor(key, { isManagerMode: true, isActiveJobMode: false }));
    }
  }
  return index;
};

const capitalise = (text: string): string => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text);

/**
 * What a step says before — or instead of — CoSMo. Only what the element itself makes plain:
 * no guess about what pressing it does, which is the rule every curated step follows too.
 */
export const labelSentence = (control: ScannedControl): { title: string; body: string } => {
  const name = control.label || "This part of the screen";
  const quoted = `"${control.label}"`;
  const bodies: Record<string, string> = {
    button: `The ${quoted} button.`,
    link: `A link: ${quoted}.`,
    tab: `The ${quoted} tab. Press it to switch to that view.`,
    dropdown: `A dropdown: ${quoted}. Open it to pick from a list.`,
    checkbox: `A checkbox: ${quoted}.`,
    switch: `A switch: ${quoted}.`,
    option: `An option: ${quoted}.`,
    "menu item": `A menu item: ${quoted}.`,
    "search box": `A search box: ${quoted}. Type into it to search.`,
    "text box": `A box to type into: ${quoted}.`,
    "number box": `A box for a number: ${quoted}.`,
    "date picker": `A date picker: ${quoted}.`,
    "time picker": `A time picker: ${quoted}.`,
    "file picker": `Choose a file here: ${quoted}.`,
    slider: `A slider: ${quoted}.`,
  };

  let body = control.label ? bodies[control.kind] ?? `${quoted}.` : "This part of the screen.";
  if (control.state?.includes("disabled")) body += " It isn't available right now.";

  return { title: capitalise(name), body };
};

/**
 * Turn a scan into a tour, and say what still needs CoSMo.
 *
 * `screenTitle` is a `routes.ts` title ("the Images screen"), used only for the tour's label.
 */
export const planTour = (
  controls: readonly ScannedControl[],
  screenKey: string | undefined,
  screenTitle: string | undefined,
  context: WalkthroughContext = {}
): TourPlan => {
  const curated = curatedIndex(screenKey, context);
  const cache = readCache();
  const toDescribe: TourControlInput[] = [];

  const steps: TourStep[] = controls.map((control) => {
    const known = control.anchor ? curated.get(control.anchor) : undefined;

    if (known) {
      return { anchor: control.anchor!, scanId: control.id, title: known.title, body: known.body, source: "curated" };
    }

    const anchor = control.anchor ?? `tour:${control.id}`;
    const remembered = cache.entries[cacheSignature(screenKey, control)];
    if (remembered) {
      return {
        anchor,
        scanId: control.id,
        title: remembered.title ?? labelSentence(control).title,
        body: remembered.body,
        source: "remembered",
      };
    }

    const fallback = labelSentence(control);

    // Nothing to ask about a control with no name: the model would have only its kind.
    if (!control.label) return { anchor, scanId: control.id, ...fallback, source: "label" };

    toDescribe.push({
      id: control.id,
      kind: control.kind,
      label: control.label,
      context: control.context,
      state: control.state,
      anchor: control.anchor,
    });
    return { anchor, scanId: control.id, ...fallback, source: "pending" };
  });

  return {
    walkthrough: {
      key: screenKey ?? "scan",
      label: screenTitle ? `Show me around ${screenTitle}` : "Show me around this screen",
      steps,
    },
    toDescribe,
  };
};

/**
 * Fold CoSMo's answer into the tour. Returns a NEW walkthrough (same key, so the overlay's step
 * effect does not re-run) and remembers each description for next time.
 *
 * `failedIds` are the ids that were asked about and will not be answered — they drop from
 * `pending` to `label`, so the card stops promising text that is not coming.
 */
export const applyDescriptions = (
  walkthrough: Walkthrough,
  controls: readonly ScannedControl[],
  screenKey: string | undefined,
  described: readonly TourControlDescription[],
  failedIds: readonly string[] = []
): Walkthrough => {
  const byId = new Map(described.map((d) => [d.id, d]));
  const failed = new Set(failedIds);
  const controlById = new Map(controls.map((c) => [c.id, c]));
  const cache = readCache();
  let wrote = false;

  const steps = walkthrough.steps.map((step) => {
    const tourStep = step as TourStep;
    if (tourStep.source !== "pending") return step;

    const answer = byId.get(tourStep.scanId);
    if (answer) {
      const control = controlById.get(tourStep.scanId);
      if (control) {
        cache.entries[cacheSignature(screenKey, control)] = { title: answer.title, body: answer.body };
        wrote = true;
      }
      return { ...tourStep, title: answer.title ?? tourStep.title, body: answer.body, source: "remembered" as const };
    }

    return failed.has(tourStep.scanId) ? { ...tourStep, source: "label" as const } : step;
  });

  if (wrote) writeCache(cache);
  return { ...walkthrough, steps };
};

/** Whether a step is still waiting for CoSMo. */
export const isPendingStep = (step: WalkthroughStep): boolean =>
  (step as Partial<TourStep>).source === "pending";

/** The scanner id of a step, when it came from a scan. */
export const scanIdOf = (step: WalkthroughStep): string | undefined => (step as Partial<TourStep>).scanId;

/**
 * Split the ask into batches: a SMALL first one so the first few steps get CoSMo's words
 * almost at once, then the rest together. All are sent in parallel.
 */
export const batchesFor = (inputs: readonly TourControlInput[], first = 5, size = 25): TourControlInput[][] => {
  if (inputs.length === 0) return [];
  const batches: TourControlInput[][] = [inputs.slice(0, first)];
  for (let i = first; i < inputs.length; i += size) batches.push(inputs.slice(i, i + size));
  return batches;
};
